import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormLabel,
	Grid,
	LinearProgress,
	Stack,
	Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { addDays } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import {
	useCreateBulkDebtorsMutation,
	usePreviewBulkDebtorsMutation,
} from '@/features/deals/dealsApi';
import { showToast } from '@/features/toast/toastSlice';
import { outStandingDaysObj, MAX_PER_API_BATCH_SIZE } from '@/utils/constants';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { OwnerMultiSelect } from '@/components/selects/OwnerSelect';
import { FundingStatusSelect } from '@/components/selects/FundingStatusSelect';
import { OutstandingDateFilterSelect } from '@/components/selects/OutstandingDateSelect';
import { parseToInsightDateString, safelyParseDate } from '@/utils/date';
import ShowPreview from '@/components/dialogs/BulkDebtorsDialog/ShowPreview';

const getErrorMessage = (error, fallback) =>
	error?.data?.message ?? error?.error ?? error?.message ?? fallback;

const calculateEffectiveRange = (asAt, range) => {
	if (!asAt || !range) return { from: null, to: safelyParseDate(asAt) };
	const [addedFrom, addedTo] = outStandingDaysObj[range];
	return {
		from: addedFrom != null ? addDays(asAt, -addedFrom) : null,
		to: addedTo != null ? addDays(asAt, -addedTo) : null,
	};
};

export const BulkDebtorsDialog = ({ open, onClose }) => {
	const dispatch = useDispatch();

	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const canViewGroup = userDetails?.canViewGroup;
	const filters = useSelector((state) => state.debtorsFilterPrefs);

	const [ownerIds, setOwnerIds] = useState([]);
	const [asAtDate, setAsAtDate] = useState(null);
	const [outstandingDate, setOutstandingDate] = useState(null);
	const [effectiveFromDate, setEffectiveFromDate] = useState(null);
	const [effectiveToDate, setEffectiveToDate] = useState(null);
	const [fundingStatus, setFundingStatus] = useState([]);
	const [preview, setPreview] = useState(null);

	const [previewBulkDebtors, previewMutation] = usePreviewBulkDebtorsMutation();
	const [createBulkDebtors, createMutation] = useCreateBulkDebtorsMutation();

	useEffect(() => {
		if (!open) return;

		setOwnerIds(filters?.ownerIds ?? []);
		setAsAtDate(filters?.asAtDate ?? new Date());
		setOutstandingDate(filters?.outstandingDate ?? null);
		setEffectiveFromDate(filters?.effectiveFromDate ?? null);
		setEffectiveToDate(
			filters?.effectiveToDate ??
				(safelyParseDate(filters?.asAtDate) ?? new Date())?.toISOString?.(),
		);
		setFundingStatus(filters?.fundingStatus ?? []);
		setPreview(null);
	}, [
		open,
		filters?.ownerIds,
		filters?.asAtDate,
		filters?.outstandingDate,
		filters?.effectiveFromDate,
		filters?.effectiveToDate,
		filters?.fundingStatus,
	]);

	const updateEffectiveRange = (asAt, range) => {
		const { from, to } = calculateEffectiveRange(asAt, range);
		setEffectiveFromDate(from?.toISOString() ?? null);
		setEffectiveToDate(to?.toISOString() ?? null);
		setOutstandingDate(range);
	};

	const handleOutstandingDaysChange = (selectedRange) => {
		const asAt = safelyParseDate(asAtDate) ?? new Date();
		if (!selectedRange) {
			setEffectiveFromDate(null);
			setOutstandingDate(null);
			setEffectiveToDate(asAt.toISOString());
		} else {
			updateEffectiveRange(asAt, selectedRange);
		}
		setPreview(null);
	};

	const handleAsAtChange = (value) => {
		if (!value) return;
		const date = safelyParseDate(value)?.toISOString?.();
		setAsAtDate(date);

		if (outstandingDate) {
			updateEffectiveRange(value, outstandingDate);
		} else {
			setEffectiveFromDate(null);
			setEffectiveToDate(date);
		}
		setPreview(null);
	};

	const payload = useMemo(
		() => ({
			...(userDetails?.authorisedRepId &&
				!canViewGroup && {
					authRepId: userDetails?.authorisedRepId,
				}),
			ownerDocIds: ownerIds,
			fundingStatus,
			effectiveFromDate: parseToInsightDateString(effectiveFromDate),
			effectiveToDate: parseToInsightDateString(effectiveToDate),
		}),
		[
			userDetails?.authorisedRepId,
			canViewGroup,
			ownerIds,
			fundingStatus,
			effectiveFromDate,
			effectiveToDate,
		],
	);

	const canPreview = userDetails && Boolean(payload.effectiveToDate);

	const canCreate =
		canPreview &&
		preview?.groups?.length > 0 &&
		!preview?.truncated &&
		!previewMutation.isLoading &&
		!createMutation.isLoading;

	const filtersDisabled = previewMutation.isLoading || createMutation.isLoading;

	const handlePreview = async () => {
		try {
			const response = await previewBulkDebtors(payload).unwrap();
			setPreview(response);
			if ((response?.groups ?? []).length === 0) {
				dispatch(
					showToast({
						message: 'No eligible debtor deals found for that range.',
						error: true,
					}),
				);
			}
		} catch (error) {
			dispatch(
				showToast({
					message: getErrorMessage(error, 'Could not preview debtor deals.'),
					error: true,
				}),
			);
		}
	};

	const handleCreate = async () => {
		try {
			const response = await createBulkDebtors(payload).unwrap();

			const createdCount = response?.created?.length ?? 0;
			const failedCount = response?.failed?.length ?? 0;

			dispatch(
				showToast({
					message:
						failedCount > 0
							? `${createdCount} debtor deals created, ${failedCount} failed.`
							: `${createdCount} debtor deals created.`,
					error: failedCount > 0,
					success: failedCount === 0,
				}),
			);
			setPreview(null);
		} catch (error) {
			dispatch(
				showToast({
					message: getErrorMessage(error, 'Could not create debtor deals.'),
					error: true,
				}),
			);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
			<DialogTitle>Create bulk debtors</DialogTitle>
			<DialogContent sx={{ p: 3 }}>
				<Stack spacing={2.5}>
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						spacing={2}
						alignItems={{ xs: 'stretch', sm: 'flex-end' }}
						justifyContent='center'
					>
						<Grid
							container
							spacing={2}
							alignItems='flex-start'
							sx={{
								mt: 1,
								width: '100%',
								'& .MuiFormControl-root, & .MuiAutocomplete-root': {
									width: '100%',
								},
								'& .MuiInputBase-root': {
									height: 40,
									minHeight: 40,
									boxSizing: 'border-box',
									flexWrap: 'nowrap',
									overflow: 'hidden',
								},
								'& .MuiOutlinedInput-input, & .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
									{
										padding: '8px 14px',
										backgroundColor: 'transparent !important',
									},
								'& .MuiAutocomplete-root .MuiOutlinedInput-root': {
									padding: '0 9px',
								},
								'& .MuiChip-root': {
									height: 24,
								},
							}}
						>
							<Grid size={12}>
								<Typography>
									A maximum of {MAX_PER_API_BATCH_SIZE} debtors can be created at
									one time. Please run outside of work hours to limit the impact
									on ledger performance.
								</Typography>
							</Grid>

							{canViewGroup && (
								<Grid size={{ xs: 12, md: 3 }}>
									<FormControl fullWidth>
										<FormLabel>Client Owner</FormLabel>
										<OwnerMultiSelect
											users={ownerIds}
											disabled={!userDetails?.canViewGroup || filtersDisabled}
											onChange={(newUsers) => {
												setOwnerIds(newUsers);
												setPreview(null);
											}}
											returnValue='id'
											disableClearable={true}
										/>
									</FormControl>
								</Grid>
							)}

							<Grid size={{ xs: 12, md: canViewGroup ? 3 : 4 }}>
								<FormControl fullWidth>
									<FormLabel>As at</FormLabel>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											name='asAt'
											sx={{ width: '100%' }}
											disabled={filtersDisabled}
											format='dd/MM/yyyy'
											value={asAtDate ? new Date(asAtDate) : new Date()}
											onChange={handleAsAtChange}
										/>
									</LocalizationProvider>
								</FormControl>
							</Grid>

							<Grid size={{ xs: 12, md: canViewGroup ? 3 : 4 }}>
								<FormControl fullWidth>
									<FormLabel>Outstanding days</FormLabel>
									<OutstandingDateFilterSelect
										outstandingDate={outstandingDate}
										onChange={handleOutstandingDaysChange}
										disabled={filtersDisabled}
									/>
								</FormControl>
							</Grid>

							<Grid size={{ xs: 12, md: canViewGroup ? 3 : 4 }}>
								<FundingStatusSelect
									fundingStatus={fundingStatus}
									onChange={(newValue) => {
										setFundingStatus(newValue);
										setPreview(null);
									}}
									disabled={filtersDisabled}
								/>
							</Grid>
						</Grid>

						<Box sx={{ pb: 0.75 }} display='flex' justifyContent='center'>
							<Button
								variant='contained'
								onClick={handlePreview}
								disabled={
									!canPreview ||
									previewMutation.isLoading ||
									createMutation.isLoading
								}
							>
								{previewMutation.isLoading ? (
									<CircularProgress size={18} px={2} color='inherit' />
								) : (
									'Preview'
								)}
							</Button>
						</Box>
					</Stack>

					{(previewMutation.isLoading || createMutation.isLoading) && <LinearProgress />}

					{preview?.truncated && (
						<Alert severity='warning'>
							This range matched more transaction pages than can be safely processed
							in one run.
						</Alert>
					)}

					{preview && <ShowPreview preview={preview} />}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onClose}
					disabled={previewMutation.isLoading || createMutation.isLoading}
				>
					Cancel
				</Button>
				<Button variant='contained' onClick={handleCreate} disabled={!canCreate}>
					{createMutation.isLoading ? (
						<CircularProgress size={18} color='inherit' />
					) : preview?.groups?.length ? (
						`Create ${preview.groups.length} debtors`
					) : (
						'Create debtors'
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
BulkDebtorsDialog.displayName = 'BulkDebtorsDialog';
